import { api, ApiError, type MessageInput, type MessageResult, type UserOut } from "./api";

export type MessageOperation = { userId: number; workshopId: number; payload: MessageInput; outcomeUnknown?: boolean };
const key = (user: Pick<UserOut, "id" | "workshop_id">) => `avtodaftar_mechanic_message_v1:${user.id}:${user.workshop_id}`;
export function pendingMessage(user: Pick<UserOut, "id" | "workshop_id">): MessageOperation | null {
  try {
    const value = JSON.parse(localStorage.getItem(key(user)) ?? "null");
    // A stored in-flight request may have committed before the WebView closed.
    return value?.userId === user.id && value.workshopId === user.workshop_id && value.payload?.send_uuid ? { ...value, outcomeUnknown: true } : null;
  } catch { return null; }
}
export function freezeMessage(user: UserOut, payload: MessageInput): MessageOperation {
  const previous = pendingMessage(user);
  if (previous) return previous;
  const operation = { userId: user.id, workshopId: user.workshop_id!, payload };
  localStorage.setItem(key(user), JSON.stringify(operation));
  return operation;
}
const running = new Map<string, Promise<MessageResult>>();
export function reconcileMessage(operation: MessageOperation): Promise<MessageResult> {
  const identity = key({ id: operation.userId, workshop_id: operation.workshopId });
  const current = running.get(identity);
  if (current) return current;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15_000);
  const request = api.sendMessage(operation.payload, controller.signal).then((result) => {
    localStorage.removeItem(identity);
    return result;
  }).catch((error) => {
    const ambiguous = !(error instanceof ApiError) || error.status === 0 || error.status === 408 || error.status >= 500;
    const definitiveRecipientRejection = error instanceof ApiError && ["recipient_opted_out", "manual_messaging_disabled", "manual_daily_limit_reached"].includes(error.code);
    // An initial definitive rejection never reserves the user's messaging slot.
    // Auth rejection of a replay still cannot disprove an earlier commit.
    if (ambiguous) {
      operation.outcomeUnknown = true;
      localStorage.setItem(identity, JSON.stringify(operation));
    } else if (!operation.outcomeUnknown || definitiveRecipientRejection || ![401, 402, 403, 429].includes(error.status)) {
      localStorage.removeItem(identity);
    }
    throw error;
  }).finally(() => { window.clearTimeout(timeout); running.delete(identity); });
  running.set(identity, request);
  return request;
}
